import { dayjs, Duration } from 'modules/std/time'
import { TransitionOptions } from 'next-usequerystate'
import React from 'react'
import { IntervalQuery, TimeShiftDirection } from './defs'
import { useIntervalQuery } from './query'

export function makeIntervalQuery(
  duration: Duration,
  reference = Date.now()
): IntervalQuery {
  const endOf = endOfExclusive(reference, duration)
  const startOf = shiftTime(endOf, duration, 'past')
  return {
    from: startOf,
    duration
  }
}

/**
 * Shift a point in time by a given duration in a given direction.
 *
 * Useful for previous/next
 *
 * @param reference A reference point in time
 * @param duration How much to move in time
 * @param direction Which way to move in time
 */
export function shiftTime(
  reference: number,
  duration: Duration,
  direction: TimeShiftDirection = 'future'
): number {
  // Replace N * 24h by N day to handle daylight saving (some days have 23-25h)
  if (duration.unit === 'hour' && duration.count % 24 === 0) {
    // Keep outsite references by reassigning the whole object
    duration = { count: duration.count / 24, unit: 'day' }
  }
  const factor = direction === 'future' ? 1 : -1
  return dayjs(reference)
    .add(factor * duration.count, duration.unit)
    .valueOf()
}

/**
 * Move the given reference point in time to the end of the given duration.
 * This is actually the start of the next duration, for several reasons:
 * - We use exclusive upper-bound querying
 * - It makes it much easier to iterate over queries (from = to & shift to forward)
 */
export function endOfExclusive(reference: number, duration: Duration): number {
  return dayjs(reference).endOf(duration.unit).add(1, 'ms').valueOf()
}

// --

const _defaultDuration = {
  count: 7,
  unit: 'day'
} as const

const transitionOptions: TransitionOptions = {
  scroll: false
}

export function useTimeNavigation(
  defaultDuration: Duration = _defaultDuration
) {
  const [intervalQuery, setIntervalQuery] = useIntervalQuery(
    makeIntervalQuery(defaultDuration)
  )

  const prev = React.useCallback(() => {
    setIntervalQuery(
      ({ from, duration }) => ({
        from: shiftTime(from, duration, 'past')
      }),
      transitionOptions
    )
  }, [setIntervalQuery])

  const isNextDisabled = React.useMemo(() => {
    const now = Date.now()
    const nextFrom = shiftTime(
      intervalQuery.from,
      intervalQuery.duration,
      'future'
    )
    return nextFrom > now
  }, [intervalQuery])

  const next = React.useCallback(() => {
    setIntervalQuery(
      ({ from, duration }) => ({
        from: shiftTime(from, duration, 'future')
      }),
      transitionOptions
    )
  }, [setIntervalQuery])

  const resetAroundToday = React.useCallback(() => {
    setIntervalQuery(
      ({ duration }) => makeIntervalQuery(duration),
      transitionOptions
    )
  }, [setIntervalQuery])

  const setDuration = React.useCallback(
    (duration: Duration) => {
      setIntervalQuery(makeIntervalQuery(duration), transitionOptions)
    },
    [setIntervalQuery]
  )

  const apply = React.useCallback(
    (query: IntervalQuery) => {
      if (query.duration.unit === 'hour') {
        return Promise.resolve(false)
      }
      return setIntervalQuery(query, transitionOptions)
    },
    [setIntervalQuery]
  )

  return {
    intervalQuery,
    isNextDisabled,
    prev,
    next,
    setDuration,
    resetAroundToday,
    setIntervalQuery: apply
  }
}
